import _defineProperty from "/home/runner/work/experian-public-site-tng/experian-public-site-tng/node_modules/@babel/runtime/helpers/esm/defineProperty.js";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { timeStampNow, Observable, timeStampToClocks, relativeToClocks, generateUUID } from '@datadog/browser-core';
import { isNodeShadowHost } from '../../browser/htmlDomUtils';
import { ActionType } from '../../rawRumEvent.types';
import { PAGE_ACTIVITY_VALIDATION_DELAY, waitPageActivityEnd } from '../waitPageActivityEnd';
import { getSelectorFromElement } from '../getSelectorFromElement';
import { getNodePrivacyLevel } from '../privacy';
import { NodePrivacyLevel } from '../privacyConstants';
import { startEventTracker } from '../eventTracker';
import { createClickChain } from './clickChain';
import { getActionNameFromElement } from './getActionNameFromElement';
import { listenActionEvents } from './listenActionEvents';
import { computeFrustration } from './computeFrustration';
import { CLICK_ACTION_MAX_DURATION, updateInteractionSelector } from './interactionSelectorCache';
import { isActionChildEvent } from './isActionChildEvent';
export function trackClickActions(lifeCycle, domMutationObservable, windowOpenObservable, configuration) {
  var actionTracker = startEventTracker(lifeCycle);
  var stopObservable = new Observable();
  var currentClickChain;
  lifeCycle.subscribe(5 /* LifeCycleEventType.VIEW_ENDED */, stopClickChain);
  lifeCycle.subscribe(11 /* LifeCycleEventType.PAGE_MAY_EXIT */, stopClickChain);
  var _listenActionEvents = listenActionEvents(configuration, {
      onPointerDown: function onPointerDown(pointerDownEvent) {
        return processPointerDown(configuration, lifeCycle, domMutationObservable, pointerDownEvent, windowOpenObservable);
      },
      onPointerUp: function onPointerUp(_ref, startEvent, getUserActivity) {
        var clickActionBase = _ref.clickActionBase,
          hadActivityOnPointerDown = _ref.hadActivityOnPointerDown;
        startClickAction(configuration, lifeCycle, domMutationObservable, windowOpenObservable, actionTracker, stopObservable, appendClickToClickChain, clickActionBase, startEvent, getUserActivity, hadActivityOnPointerDown);
      }
    }),
    stopActionEventsListener = _listenActionEvents.stop;
  return {
    stop: function stop() {
      stopClickChain();
      stopObservable.notify();
      stopActionEventsListener();
      actionTracker.stopAll();
    },
    findActionId: actionTracker.findId
  };
  function appendClickToClickChain(click) {
    if (!currentClickChain || !currentClickChain.tryAppend(click)) {
      var rageClick = click.clone();
      currentClickChain = createClickChain(click, function (clicks) {
        finalizeClicks(clicks, rageClick);
        // Clear the reference to allow garbage collection. Without this, the finalize callback
        // retains a closure reference to the old click chain, preventing it from being cleaned up
        // and causing a memory leak as click chains accumulate over time.
        currentClickChain = undefined;
      });
    }
  }
  function stopClickChain() {
    if (currentClickChain) {
      currentClickChain.stop();
    }
  }
}
function processPointerDown(configuration, lifeCycle, domMutationObservable, pointerDownEvent, windowOpenObservable) {
  var targetForPrivacy = configuration.betaTrackActionsInShadowDom ? getEventTarget(pointerDownEvent) : pointerDownEvent.target;
  var nodePrivacyLevel;
  if (configuration.enablePrivacyForActionName) {
    nodePrivacyLevel = getNodePrivacyLevel(targetForPrivacy, configuration.defaultPrivacyLevel);
  } else {
    nodePrivacyLevel = NodePrivacyLevel.ALLOW;
  }
  if (nodePrivacyLevel === NodePrivacyLevel.HIDDEN) {
    return undefined;
  }
  var clickActionBase = computeClickActionBase(pointerDownEvent, nodePrivacyLevel, configuration);
  var _hadActivityOnPointerDown = false;
  waitPageActivityEnd(lifeCycle, domMutationObservable, windowOpenObservable, configuration, function (pageActivityEndEvent) {
    _hadActivityOnPointerDown = pageActivityEndEvent.hadActivity;
  },
  // We don't care about the activity duration, we just want to know whether an activity did happen
  // within the "validation delay" or not. Limit the duration so the callback is called sooner.
  PAGE_ACTIVITY_VALIDATION_DELAY);
  return {
    clickActionBase: clickActionBase,
    hadActivityOnPointerDown: function hadActivityOnPointerDown() {
      return _hadActivityOnPointerDown;
    }
  };
}
function startClickAction(configuration, lifeCycle, domMutationObservable, windowOpenObservable, actionTracker, stopObservable, appendClickToClickChain, clickActionBase, startEvent, getUserActivity, hadActivityOnPointerDown) {
  var _a;
  var click = newClick(lifeCycle, actionTracker, getUserActivity, clickActionBase, startEvent);
  appendClickToClickChain(click);
  var selector = (_a = clickActionBase === null || clickActionBase === void 0 ? void 0 : clickActionBase.target) === null || _a === void 0 ? void 0 : _a.selector;
  if (selector) {
    updateInteractionSelector(startEvent.timeStamp, selector);
  }
  var _waitPageActivityEnd = waitPageActivityEnd(lifeCycle, domMutationObservable, windowOpenObservable, configuration, function (pageActivityEndEvent) {
      if (pageActivityEndEvent.hadActivity && pageActivityEndEvent.end < click.startClocks.timeStamp) {
        // If the clock is looking weird, just discard the click
        click.discard();
      } else {
        if (pageActivityEndEvent.hadActivity) {
          click.stop(pageActivityEndEvent.end);
        } else if (hadActivityOnPointerDown()) {
          click.stop(
          // using the click start as activity end, so the click will have some activity but its
          // duration will be 0 (as the activity started before the click start)
          click.startClocks.timeStamp);
        } else {
          click.stop();
        }
      }
    }, CLICK_ACTION_MAX_DURATION),
    stopWaitPageActivityEnd = _waitPageActivityEnd.stop;
  var viewEndedSubscription = lifeCycle.subscribe(5 /* LifeCycleEventType.VIEW_ENDED */, function (_ref2) {
    var endClocks = _ref2.endClocks;
    click.stop(endClocks.timeStamp);
  });
  var pageMayExitSubscription = lifeCycle.subscribe(11 /* LifeCycleEventType.PAGE_MAY_EXIT */, function () {
    click.stop(timeStampNow());
  });
  var stopSubscription = stopObservable.subscribe(function () {
    click.stop();
  });
  click.stopObservable.subscribe(function () {
    pageMayExitSubscription.unsubscribe();
    viewEndedSubscription.unsubscribe();
    stopWaitPageActivityEnd();
    stopSubscription.unsubscribe();
  });
}
function computeClickActionBase(event, nodePrivacyLevel, configuration) {
  var target = configuration.betaTrackActionsInShadowDom ? getEventTarget(event) : event.target;
  var rect = target.getBoundingClientRect();
  var selector = getSelectorFromElement(target, configuration.actionNameAttribute);
  if (selector) {
    updateInteractionSelector(event.timeStamp, selector);
  }
  var _getActionNameFromEle = getActionNameFromElement(target, configuration, nodePrivacyLevel),
    name = _getActionNameFromEle.name,
    nameSource = _getActionNameFromEle.nameSource;
  return {
    type: ActionType.CLICK,
    target: {
      width: Math.round(rect.width),
      height: Math.round(rect.height),
      selector: selector
    },
    position: {
      // Use clientX and Y because for SVG element offsetX and Y are relatives to the <svg> element
      x: Math.round(event.clientX - rect.left),
      y: Math.round(event.clientY - rect.top)
    },
    name: name,
    nameSource: nameSource
  };
}
function getEventTarget(event) {
  if (event.composed && isNodeShadowHost(event.target) && typeof event.composedPath === 'function') {
    var composedPath = event.composedPath();
    if (composedPath.length > 0 && composedPath[0] instanceof Element) {
      return composedPath[0];
    }
  }
  return event.target;
}
function newClick(lifeCycle, actionTracker, getUserActivity, clickActionBase, startEvent) {
  var clickKey = generateUUID();
  var startClocks = relativeToClocks(startEvent.timeStamp);
  var startedClickAction = actionTracker.start(clickKey, startClocks, clickActionBase, {
    isChildEvent: isActionChildEvent
  });
  lifeCycle.notify(15 /* LifeCycleEventType.ACTION_STARTED */, startedClickAction);
  var status = 0 /* ClickStatus.ONGOING */;
  var actionTrackerFinishedEvent;
  var frustrationTypes = [];
  var stopObservable = new Observable();
  function stop(activityEndTime) {
    if (status !== 0 /* ClickStatus.ONGOING */) {
      return;
    }
    status = 1 /* ClickStatus.STOPPED */;
    actionTrackerFinishedEvent = activityEndTime ? actionTracker.stop(clickKey, timeStampToClocks(activityEndTime)) : actionTracker.discard(clickKey);
    stopObservable.notify();
  }
  return {
    event: startEvent,
    stop: stop,
    stopObservable: stopObservable,
    get hasError() {
      var _a;
      var currentCounts = (_a = actionTrackerFinishedEvent === null || actionTrackerFinishedEvent === void 0 ? void 0 : actionTrackerFinishedEvent.counts) !== null && _a !== void 0 ? _a : actionTracker.getCounts(clickKey);
      return currentCounts ? currentCounts.errorCount > 0 : false;
    },
    get hasPageActivity() {
      return actionTrackerFinishedEvent && 'duration' in actionTrackerFinishedEvent;
    },
    getUserActivity: getUserActivity,
    addFrustration: function addFrustration(frustrationType) {
      frustrationTypes.push(frustrationType);
    },
    get startClocks() {
      return startClocks;
    },
    isStopped: function isStopped() {
      return status === 1 /* ClickStatus.STOPPED */ || status === 2;
    } /* ClickStatus.FINALIZED */,
    clone: function clone() {
      return newClick(lifeCycle, actionTracker, getUserActivity, clickActionBase, startEvent);
    },
    validate: function validate(domEvents) {
      stop();
      if (status !== 1 /* ClickStatus.STOPPED */) {
        return;
      }
      if (!actionTrackerFinishedEvent) {
        return;
      }
      var clickAction = _objectSpread(_objectSpread({
        frustrationTypes: frustrationTypes,
        events: domEvents !== null && domEvents !== void 0 ? domEvents : [startEvent],
        event: startEvent
      }, actionTrackerFinishedEvent), {}, {
        counts: actionTrackerFinishedEvent.counts // This is needed to satisfy the type checker
      });
      lifeCycle.notify(0 /* LifeCycleEventType.AUTO_ACTION_COMPLETED */, clickAction);
      status = 2 /* ClickStatus.FINALIZED */;
    },
    discard: function discard() {
      stop();
      status = 2 /* ClickStatus.FINALIZED */;
    }
  };
}
export function finalizeClicks(clicks, rageClick) {
  var _computeFrustration = computeFrustration(clicks, rageClick),
    isRage = _computeFrustration.isRage;
  if (isRage) {
    clicks.forEach(function (click) {
      return click.discard();
    });
    rageClick.stop(timeStampNow());
    rageClick.validate(clicks.map(function (click) {
      return click.event;
    }));
  } else {
    rageClick.discard();
    clicks.forEach(function (click) {
      return click.validate();
    });
  }
}